import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { EmergencyProvider } from './context/EmergencyContext';
import ProtectedRoute from './components/ProtectedRoute';
import BrandFavicon from './components/BrandFavicon';
import { LanguageProvider } from './i18n/LanguageContext';
import PassportVerify from './pages/verify/PassportVerify';
import Signup from './pages/signup/Signup';
import SignupSuccess from './pages/signup/SignupSuccess';
import BillingRequired from './pages/signup/BillingRequired';

// Auth
import Login from './pages/auth/Login';
import PasswordReset from './pages/auth/PasswordReset';
import EmailVerification from './pages/auth/EmailVerification';
import InvitationAcceptance from './pages/auth/InvitationAcceptance';
import OnboardingAdmin from './pages/auth/OnboardingAdmin';
import OnboardingManager from './pages/auth/OnboardingManager';
import OnboardingOperator from './pages/auth/OnboardingOperator';

// Dashboard
import GroupOverview from './pages/dashboard/GroupOverview';
import SiteDashboard from './pages/dashboard/SiteDashboard';
import HqDiagnostic from './pages/diagnostic/HqDiagnostic';

// Machines
import MachineList from './pages/machines/MachineList';
import MachineOnboarding from './pages/machines/MachineOnboarding';
import MachineProfile from './pages/machines/MachineProfile';
import ServiceRecordNew from './pages/service/ServiceRecordNew';
import ServiceRecordDetail from './pages/service/ServiceRecordDetail';

// Staff
import StaffList from './pages/staff/StaffList';
import StaffInvite from './pages/staff/StaffInvite';
import StaffProfile from './pages/staff/StaffProfile';
import QualificationNew from './pages/staff/QualificationNew';

// Checks
import Checks from './pages/checks/Checks';
import CheckDetail from './pages/checks/CheckDetail';
import CheckTemplateBuilder from './pages/checks/CheckTemplateBuilder';

// Shifts
import ShiftNew from './pages/shifts/ShiftNew';
import ShiftDetail from './pages/shifts/ShiftDetail';

// Safety
import Safety from './pages/safety/Safety';
import DefectDetail from './pages/defects/DefectDetail';
import NearMissDetail from './pages/nearMisses/NearMissDetail';
import IncidentDetail from './pages/incidents/IncidentDetail';
import EmergencyDetail from './pages/emergencies/EmergencyDetail';

// Operations
import Operations from './pages/operations/Operations';
import DervDetail from './pages/fuel/DervDetail';
import JobNew from './pages/jobs/JobNew';
import JobDetail from './pages/jobs/JobDetail';
import AnnouncementNew from './pages/announcements/AnnouncementNew';
import AnnouncementDetail from './pages/announcements/AnnouncementDetail';

// Compliance
import CoshhRegister from './pages/coshh/CoshhRegister';
import CoshhDetail from './pages/coshh/CoshhDetail';
import RiskAssessmentList from './pages/riskAssessments/RiskAssessmentList';
import RiskAssessmentDetail from './pages/riskAssessments/RiskAssessmentDetail';

// Reports
import ReportsHub from './pages/reports/ReportsHub';
import FleetHealthReport from './pages/reports/FleetHealthReport';
import ComplianceReport from './pages/reports/ComplianceReport';
import QualificationReport from './pages/reports/QualificationReport';
import ServiceDueReport from './pages/reports/ServiceDueReport';
import FuelUsageReport from './pages/reports/FuelUsageReport';
import CarbonReport from './pages/reports/CarbonReport';
import CostReport from './pages/reports/CostReport';
import InsurancePack from './pages/reports/InsurancePack';

// Settings
import Organisation from './pages/settings/Organisation';
import Sites from './pages/settings/Sites';
import SiteDetail from './pages/settings/SiteDetail';
import Roles from './pages/settings/Roles';
import Billing from './pages/settings/Billing';
import Notifications from './pages/settings/Notifications';
import AuditLog from './pages/settings/AuditLog';
import Data from './pages/settings/Data';

// Profile
import PersonalDetails from './pages/profile/PersonalDetails';
import MyQualifications from './pages/profile/MyQualifications';
import ProfileNotifications from './pages/profile/ProfileNotifications';
import Security from './pages/profile/Security';
import Sessions from './pages/profile/Sessions';
import Integrations from './pages/settings/Integrations';
import SettingsIndex from './pages/settings/SettingsIndex';

import ToastContainer from './components/ui/Toast';

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <LanguageProvider>
        <BrandFavicon />
        <EmergencyProvider>
          <Routes>
            {/* Public */}
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/signup/success" element={<SignupSuccess />} />
            <Route path="/billing-required" element={<BillingRequired />} />
            <Route path="/reset-password" element={<PasswordReset />} />
            <Route path="/verify-email" element={<EmailVerification />} />
            <Route path="/invite/:inviteId" element={<InvitationAcceptance />} />
            <Route path="/verify" element={<PassportVerify />} />
            <Route path="/verify/:reference" element={<PassportVerify />} />

            {/* Onboarding */}
            <Route path="/onboarding/admin" element={<ProtectedRoute><OnboardingAdmin /></ProtectedRoute>} />
            <Route path="/onboarding/manager" element={<ProtectedRoute><OnboardingManager /></ProtectedRoute>} />
            <Route path="/onboarding/operator" element={<ProtectedRoute><OnboardingOperator /></ProtectedRoute>} />

            {/* Dashboard */}
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="/dashboard" element={<ProtectedRoute><GroupOverview /></ProtectedRoute>} />
            <Route path="/hq-diagnostic" element={<ProtectedRoute><HqDiagnostic /></ProtectedRoute>} />
            <Route path="/dashboard/site" element={<ProtectedRoute><SiteDashboard /></ProtectedRoute>} />

            {/* Machines */}
            <Route path="/machines" element={<ProtectedRoute><MachineList /></ProtectedRoute>} />
            <Route path="/machines/new" element={<ProtectedRoute><MachineOnboarding /></ProtectedRoute>} />
            <Route path="/machines/:machineId" element={<ProtectedRoute><MachineProfile /></ProtectedRoute>} />
            <Route path="/machines/:machineId/service/new" element={<ProtectedRoute><ServiceRecordNew /></ProtectedRoute>} />
            <Route path="/machines/:machineId/service/:recordId" element={<ProtectedRoute><ServiceRecordDetail /></ProtectedRoute>} />

            {/* Staff */}
            <Route path="/staff" element={<ProtectedRoute><StaffList /></ProtectedRoute>} />
            <Route path="/staff/invite" element={<ProtectedRoute><StaffInvite /></ProtectedRoute>} />
            <Route path="/staff/:userId" element={<ProtectedRoute><StaffProfile /></ProtectedRoute>} />
            <Route path="/staff/:userId/qualifications/new" element={<ProtectedRoute><QualificationNew /></ProtectedRoute>} />

            {/* Shifts */}
            <Route path="/shifts/new" element={<ProtectedRoute><ShiftNew /></ProtectedRoute>} />
            <Route path="/shifts/:shiftId" element={<ProtectedRoute><ShiftDetail /></ProtectedRoute>} />

            {/* Checks */}
            <Route path="/checks" element={<ProtectedRoute><Checks /></ProtectedRoute>} />
            <Route path="/checks/templates/new" element={<ProtectedRoute><CheckTemplateBuilder /></ProtectedRoute>} />
            <Route path="/checks/templates/:templateId/edit" element={<ProtectedRoute><CheckTemplateBuilder /></ProtectedRoute>} />
            <Route path="/checks/:checkId" element={<ProtectedRoute><CheckDetail /></ProtectedRoute>} />

            {/* Safety */}
            <Route path="/safety" element={<ProtectedRoute><Safety /></ProtectedRoute>} />
            <Route path="/defects/:defectId" element={<ProtectedRoute><DefectDetail /></ProtectedRoute>} />
            <Route path="/near-misses/:nearMissId" element={<ProtectedRoute><NearMissDetail /></ProtectedRoute>} />
            <Route path="/incidents/:incidentId" element={<ProtectedRoute><IncidentDetail /></ProtectedRoute>} />
            <Route path="/emergencies/:emergencyId" element={<ProtectedRoute><EmergencyDetail /></ProtectedRoute>} />

            {/* Operations */}
            <Route path="/operations" element={<ProtectedRoute><Operations /></ProtectedRoute>} />
            <Route path="/fuel/:tankId" element={<ProtectedRoute><DervDetail /></ProtectedRoute>} />
            <Route path="/jobs/new" element={<ProtectedRoute><JobNew /></ProtectedRoute>} />
            <Route path="/jobs/:jobId" element={<ProtectedRoute><JobDetail /></ProtectedRoute>} />
            <Route path="/announcements/new" element={<ProtectedRoute><AnnouncementNew /></ProtectedRoute>} />
            <Route path="/announcements/:announcementId" element={<ProtectedRoute><AnnouncementDetail /></ProtectedRoute>} />

            {/* Compliance (within Settings) */}
            <Route path="/settings/coshh" element={<ProtectedRoute><CoshhRegister /></ProtectedRoute>} />
            <Route path="/settings/coshh/:coshhId" element={<ProtectedRoute><CoshhDetail /></ProtectedRoute>} />
            <Route path="/settings/risk-assessments" element={<ProtectedRoute><RiskAssessmentList /></ProtectedRoute>} />
            <Route path="/settings/risk-assessments/:raId" element={<ProtectedRoute><RiskAssessmentDetail /></ProtectedRoute>} />

            {/* Reports (within Settings) */}
            {/* Reports are weekly working tools, not configuration, so they
                live at the top level and the sidebar shows them to managers
                as well as admins. The old settings paths redirect so any
                bookmark still lands in the right place. */}
            <Route path="/reports" element={<ProtectedRoute><ReportsHub /></ProtectedRoute>} />
            <Route path="/reports/fleet" element={<ProtectedRoute><FleetHealthReport /></ProtectedRoute>} />
            <Route path="/reports/compliance" element={<ProtectedRoute><ComplianceReport /></ProtectedRoute>} />
            <Route path="/reports/qualifications" element={<ProtectedRoute><QualificationReport /></ProtectedRoute>} />
            <Route path="/reports/service-due" element={<ProtectedRoute><ServiceDueReport /></ProtectedRoute>} />
            <Route path="/reports/fuel" element={<ProtectedRoute><FuelUsageReport /></ProtectedRoute>} />
            <Route path="/reports/carbon" element={<ProtectedRoute><CarbonReport /></ProtectedRoute>} />
            <Route path="/reports/cost" element={<ProtectedRoute><CostReport /></ProtectedRoute>} />
            <Route path="/reports/insurance" element={<ProtectedRoute><InsurancePack /></ProtectedRoute>} />
            <Route path="/settings/reports" element={<Navigate to="/reports" replace />} />
            <Route path="/settings/reports/fleet" element={<Navigate to="/reports/fleet" replace />} />
            <Route path="/settings/reports/compliance" element={<Navigate to="/reports/compliance" replace />} />
            <Route path="/settings/reports/qualifications" element={<Navigate to="/reports/qualifications" replace />} />
            <Route path="/settings/reports/service-due" element={<Navigate to="/reports/service-due" replace />} />
            <Route path="/settings/reports/fuel" element={<Navigate to="/reports/fuel" replace />} />
            <Route path="/settings/reports/carbon" element={<Navigate to="/reports/carbon" replace />} />
            <Route path="/settings/reports/cost" element={<Navigate to="/reports/cost" replace />} />
            <Route path="/settings/reports/insurance" element={<Navigate to="/reports/insurance" replace />} />

            {/* Settings */}
            <Route path="/settings" element={<ProtectedRoute><SettingsIndex /></ProtectedRoute>} />
            <Route path="/settings/organisation" element={<ProtectedRoute><Organisation /></ProtectedRoute>} />
            <Route path="/settings/sites" element={<ProtectedRoute><Sites /></ProtectedRoute>} />
            <Route path="/settings/sites/:siteId" element={<ProtectedRoute><SiteDetail /></ProtectedRoute>} />
            <Route path="/settings/roles" element={<ProtectedRoute><Roles /></ProtectedRoute>} />
            <Route path="/settings/billing" element={<ProtectedRoute><Billing /></ProtectedRoute>} />
            <Route path="/settings/notifications" element={<ProtectedRoute><Notifications /></ProtectedRoute>} />
            <Route path="/settings/audit-log" element={<ProtectedRoute><AuditLog /></ProtectedRoute>} />
            <Route path="/settings/data" element={<ProtectedRoute><Data /></ProtectedRoute>} />

            {/* Profile */}
            <Route path="/profile" element={<ProtectedRoute><PersonalDetails /></ProtectedRoute>} />
            <Route path="/profile/qualifications" element={<ProtectedRoute><MyQualifications /></ProtectedRoute>} />
            <Route path="/settings/integrations" element={<ProtectedRoute><Integrations /></ProtectedRoute>} />
            <Route path="/profile/notifications" element={<ProtectedRoute><ProfileNotifications /></ProtectedRoute>} />
            <Route path="/profile/security" element={<ProtectedRoute><Security /></ProtectedRoute>} />
            <Route path="/profile/sessions" element={<ProtectedRoute><Sessions /></ProtectedRoute>} />

            {/* Fallback */}
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
          <ToastContainer />
        </EmergencyProvider>
        </LanguageProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;